<template>
  <v-container fluid>
    <base-material-card
      color="primary"
      icon="mdi-file-check-outline"
      inline
      class="px-5 py-5"
    >
      <template v-slot:after-heading>
        <div class="display-1 font-weight-light">
          Novo Teste de Conformidade de Obras
        </div>
      </template>

      <v-progress-linear
        v-if="loadingConfig"
        indeterminate
        color="primary"
        class="mt-6"
      />

      <v-form
        v-else
        class="pt-5"
      >
        <v-row
          v-if="testeRequerUpload"
          class="mx-4"
        >
          <v-col cols="12">
            <v-alert
              dense
              outlined
              type="info"
              color="blue"
            >
              <div class="font-weight-medium mb-1">
                Contrato dos arquivos selecionados:
              </div>
              <div
                v-for="item in contratosUploadSelecionados"
                :key="item.codigo"
                class="text-body-2"
              >
                <strong>{{ item.nome }}:</strong> {{ item.contrato_upload }}
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <v-row
          v-else-if="testesSelecionados.length"
          class="mx-4"
        >
          <v-col cols="12">
            <v-alert
              dense
              outlined
              type="info"
              color="blue"
            >
              Os testes selecionados usam apenas bases internas do sistema e não requerem upload de arquivo.
            </v-alert>
          </v-col>
        </v-row>

        <v-row
          align="center"
          no-gutters
          class="mx-4"
        >
          <v-col cols="12" md="8">
            <v-text-field
              class="mx-2"
              v-model="form.nome"
              label="Nome base das execuções*"
              outlined
              persistent-hint
              hint="Quando houver mais de um teste selecionado, o sistema acrescentará o nome do teste em cada execução criada."
              :error-messages="fieldErrors.nome"
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              class="mx-2"
              v-model="form.grupo"
              :items="tiposTeste"
              label="Tipo de teste*"
              outlined
              item-text="text"
              item-value="value"
              persistent-hint
              hint="Selecione o tipo para exibir apenas os testes compatíveis do mesmo grupo."
              :error-messages="fieldErrors.grupo"
              @change="handleChangeGrupo"
            />
          </v-col>
        </v-row>

        <v-row class="mx-4">
          <v-col cols="12">
            <div class="text-subtitle-1 font-weight-medium mb-2">
              {{ grupoSelecionadoLabel || 'Testes Disponíveis' }}
            </div>
            <v-card outlined class="pa-3">
              <div
                v-if="!form.grupo"
                class="text-body-2 grey--text text--darken-1"
              >
                Selecione primeiro o tipo de teste para liberar os checkboxes disponíveis.
              </div>

              <v-checkbox
                v-for="teste in testesDisponiveis"
                v-else
                :key="teste.codigo"
                v-model="form.teste_codigos"
                :label="teste.nome"
                :value="teste.codigo"
                hide-details="auto"
                class="mt-0"
                @change="handleSelecaoTestes"
              >
                <template v-slot:label>
                  <div>
                    <div>{{ teste.nome }}</div>
                    <div class="text-caption grey--text text--darken-1">
                      {{ teste.descricao }}
                    </div>
                  </div>
                </template>
              </v-checkbox>
            </v-card>
            <div
              v-if="fieldErrors.teste_codigos.length"
              class="error--text text-caption mt-2"
            >
              {{ fieldErrors.teste_codigos[0] }}
            </div>
          </v-col>
        </v-row>

        <v-row
          v-if="filtrosVisiveis.length"
          class="mx-4"
        >
          <v-col
            v-for="filtro in filtrosVisiveis"
            :key="filtro.campo"
            cols="12"
            md="6"
          >
            <date-picker
              v-if="filtro.tipo === 'date'"
              v-model="form.parametros_execucao[filtro.campo]"
              :label="buildFiltroLabel(filtro)"
              type="date"
              outlined
              persistent-hint
              :hint="filtro.hint"
            />
            <v-text-field
              v-else
              v-model="form.parametros_execucao[filtro.campo]"
              :label="buildFiltroLabel(filtro)"
              outlined
              persistent-hint
              :hint="filtro.hint"
            />
            <div
              v-if="getParametroError(filtro.campo)"
              class="error--text text-caption mt-1"
            >
              {{ getParametroError(filtro.campo) }}
            </div>
          </v-col>
        </v-row>

        <v-row
          v-if="testeRequerUpload"
          align="center"
          no-gutters
          class="mx-4"
        >
          <v-col cols="12">
            <v-file-input
              v-model="form.arquivos"
              label="Fichas técnicas (.xlsx, .xlsm)*"
              placeholder="Selecione uma ou mais fichas técnicas"
              outlined
              multiple
              chips
              show-size
              truncate-length="40"
              accept=".xlsx,.xlsm"
              :error-messages="fieldErrors.arquivos"
              @change="clearFieldError('arquivos')"
            />
          </v-col>
        </v-row>

        <v-row class="mx-4">
          <v-col cols="12">
            <v-btn
              color="green"
              min-width="140"
              style="float: right;"
              :loading="loadingSave"
              @click="salvar()"
            >
              Salvar
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </base-material-card>
  </v-container>
</template>

<script>
import dayjs from 'dayjs';
import ConformidadeObrasService from '@/services/ConformidadeObrasService';

export default {
  name: 'ConformidadeObrasEdit',

  components: {
    DatePicker: () => import('@/components/general/DatePicker.vue')
  },

  data() {
    return {
      loadingConfig: false,
      loadingSave: false,
      testes: [],
      form: {
        grupo: null,
        nome: '',
        teste_codigos: [],
        parametros_execucao: {},
        arquivos: []
      },
      autoSuggestedName: '',
      fieldErrors: {
        grupo: [],
        nome: [],
        teste_codigos: [],
        arquivos: []
      },
      parametroErrors: {}
    };
  },

  computed: {
    tiposTeste() {
      return [
        { text: 'Encerramento Técnico', value: 'encerramento_tecnico' },
        { text: 'Conformidade', value: 'conformidade' }
      ];
    },

    grupoSelecionadoLabel() {
      const encontrado = this.tiposTeste.find(item => item.value === this.form.grupo);
      return encontrado ? encontrado.text : '';
    },

    testesDisponiveis() {
      if (!this.form.grupo) return [];

      return this.testes.filter(item => item.grupo === this.form.grupo);
    },

    testesSelecionados() {
      return this.testes.filter(item => this.form.teste_codigos.includes(item.codigo));
    },

    testeRequerUpload() {
      return this.testesSelecionados.some(item => item.requer_upload);
    },

    contratosUploadSelecionados() {
      return this.testesSelecionados.filter(item => item.requer_upload && item.contrato_upload);
    },

    filtrosVisiveis() {
      const filtros = {};

      this.testesSelecionados.forEach((teste) => {
        (teste.filtros || []).forEach((filtro) => {
          if (!filtro || !filtro.campo) return;

          if (!filtros[filtro.campo]) {
            filtros[filtro.campo] = { ...filtro };
            return;
          }

          filtros[filtro.campo] = {
            ...filtros[filtro.campo],
            obrigatorio: filtros[filtro.campo].obrigatorio || filtro.obrigatorio,
            hint: filtros[filtro.campo].hint || filtro.hint,
            default: filtros[filtro.campo].default || filtro.default
          };
        });
      });

      const ordemFiltros = [
        'inventario_unitizacao',
        'data_unitizacao',
        'versao_blindada',
        'versao_incremental',
        'data_blindada',
        'data_incremental',
        'up_unitizacao',
        'a1_unitizacao',
        'a2_unitizacao',
        'a3_unitizacao',
        'a4_unitizacao',
        'a5_unitizacao'
      ];

      return Object.values(filtros).sort((a, b) => {
        const ordemA = ordemFiltros.indexOf(a.campo);
        const ordemB = ordemFiltros.indexOf(b.campo);
        const indiceA = ordemA === -1 ? ordemFiltros.length : ordemA;
        const indiceB = ordemB === -1 ? ordemFiltros.length : ordemB;

        return indiceA - indiceB;
      });
    }
  },

  mounted() {
    this.getConfigCriacao();
  },

  methods: {
    getConfigCriacao() {
      this.loadingConfig = true;

      ConformidadeObrasService.getConfigCriacao()
        .then((response) => {
          const data = response.data.data || {};
          this.testes = data.testes || [];
          this.form.grupo = null;
          this.syncVisibleFilterDefaults();
          this.applySuggestedName(true);
        })
        .catch((error) => {
          this.$toast.error('Erro ao carregar as configurações do teste.', '', { position: 'topRight' });
          console.error(error);
        })
        .finally(() => {
          this.loadingConfig = false;
        });
    },

    applySuggestedName(force = false) {
      const novoNome = this.buildSuggestedName();
      if (!novoNome) {
        if (force && !this.testesSelecionados.length) {
          this.form.nome = '';
        }
        return;
      }

      if (force || !this.form.nome || this.form.nome === this.autoSuggestedName) {
        this.form.nome = novoNome;
      }

      this.autoSuggestedName = novoNome;
      this.clearFieldError('nome');
      this.clearFieldError('teste_codigos');
    },

    buildSuggestedName() {
      if (!this.testesSelecionados.length) return '';

      if (this.testesSelecionados.length === 1) {
        return `${this.testesSelecionados[0].nome} - ${dayjs().format('DD/MM/YYYY HH:mm')}`;
      }

      return `Lote de Testes de Conformidade de Obras - ${dayjs().format('DD/MM/YYYY HH:mm')}`;
    },

    handleSelecaoTestes() {
      this.applySuggestedName(false);
      this.syncVisibleFilterDefaults();

      if (!this.testeRequerUpload) {
        this.form.arquivos = [];
        this.clearFieldError('arquivos');
      }

      this.clearFieldError('grupo');
      this.clearFieldError('teste_codigos');
    },

    handleChangeGrupo() {
      this.form.teste_codigos = [];
      this.form.arquivos = [];
      this.parametroErrors = {};
      this.applySuggestedName(true);
      this.syncVisibleFilterDefaults();
      this.clearFieldError('grupo');
      this.clearFieldError('teste_codigos');
      this.clearFieldError('arquivos');
    },

    syncVisibleFilterDefaults() {
      this.filtrosVisiveis.forEach((filtro) => {
        if (!Object.prototype.hasOwnProperty.call(this.form.parametros_execucao, filtro.campo)) {
          this.$set(this.form.parametros_execucao, filtro.campo, null);
        }

        if (this.form.parametros_execucao[filtro.campo]) return;
        if (!Object.prototype.hasOwnProperty.call(filtro, 'default')) return;

        this.$set(this.form.parametros_execucao, filtro.campo, filtro.default);
      });
    },

    buildFiltroLabel(filtro) {
      return filtro.obrigatorio ? `${filtro.label}*` : filtro.label;
    },

    clearFieldError(field) {
      this.fieldErrors[field] = [];
    },

    clearParametroError(field) {
      this.$delete(this.parametroErrors, field);
    },

    getParametroError(field) {
      return this.parametroErrors[field] || '';
    },

    validate() {
      let isValid = true;

      Object.keys(this.fieldErrors).forEach((field) => {
        this.fieldErrors[field] = [];
      });
      this.parametroErrors = {};

      if (!this.form.nome || !this.form.nome.trim()) {
        this.fieldErrors.nome = ['Informe o nome da execução.'];
        isValid = false;
      }

      if (!this.form.grupo) {
        this.fieldErrors.grupo = ['Selecione o tipo de teste.'];
        isValid = false;
      }

      if (!this.form.teste_codigos.length) {
        this.fieldErrors.teste_codigos = ['Selecione ao menos um teste para executar.'];
        isValid = false;
      }

      this.filtrosVisiveis.forEach((filtro) => {
        const valor = this.form.parametros_execucao[filtro.campo];
        if (filtro.obrigatorio && !valor) {
          this.$set(this.parametroErrors, filtro.campo, `Informe ${filtro.label}.`);
          isValid = false;
        }
      });

      if (this.testeRequerUpload) {
        if (!this.form.arquivos || !this.form.arquivos.length) {
          this.fieldErrors.arquivos = ['Selecione ao menos uma ficha técnica.'];
          isValid = false;
        }

        const arquivosInvalidos = (this.form.arquivos || []).filter((arquivo) => {
          return !/\.(xlsx|xlsm)$/i.test((arquivo && arquivo.name) || '');
        });

        if (arquivosInvalidos.length) {
          this.fieldErrors.arquivos = ['Apenas arquivos com extensão .xlsx e .xlsm são aceitos.'];
          isValid = false;
        }
      }

      return isValid;
    },

    uploadArquivosSelecionados() {
      if (!this.testeRequerUpload) {
        return Promise.resolve([]);
      }

      const uploadIds = [];

      return (this.form.arquivos || []).reduce((promise, arquivo) => {
        return promise.then(() => {
          const formData = new FormData();
          formData.append('file', arquivo);

          return ConformidadeObrasService.uploadArquivo(formData)
            .then((response) => {
              uploadIds.push(response.data.arquivoUploadId);
            });
        });
      }, Promise.resolve()).then(() => uploadIds);
    },

    salvar() {
      if (!this.validate()) {
        this.$toast.error('Existem campos obrigatórios pendentes no formulário.', '', { position: 'topRight' });
        return;
      }

      this.loadingSave = true;
      this.$toast.info(this.testeRequerUpload ? 'Enviando arquivos e registrando execução...' : 'Registrando execução...', '', { position: 'topRight', timeout: 2000 });

      this.uploadArquivosSelecionados()
        .then((uploadIds) => {
          return ConformidadeObrasService.salvar({
            nome: this.form.nome.trim(),
            teste_codigos: this.form.teste_codigos,
            parametros_execucao: this.form.parametros_execucao,
            arquivos_upload_ids: uploadIds
          });
        })
        .then(() => {
          this.$toast.success('Execuções de Conformidade de Obras salvas com sucesso.', '', { position: 'topRight' });
          this.$router.push({ name: 'Conformidade de Obras Histórico' });
        })
        .catch((error) => {
          const message = error?.response?.data?.erro || 'Erro ao salvar o teste de Conformidade de Obras.';
          this.$toast.error(message, '', { position: 'topRight' });
          console.error(error);
        })
        .finally(() => {
          this.loadingSave = false;
        });
    }
  }
};
</script>
